<script setup lang="ts">
import PdsBadge from '@/components/pds/PdsBadge.vue';
import PdsCheck from '@/components/pds/PdsCheck.vue';
import { computed } from 'vue';
import { formatDateTime, formatMoney } from '../format';
import type { PdsItemChange } from '../itemChanges';
import type { PdsOrderItem } from '../types';

const props = defineProps<{
  item: PdsOrderItem;
  selectable?: boolean;
  compact?: boolean;
  // Garde la hauteur d'une ligne à cocher une fois cochée : dans une liste unique, rien ne bouge quand on coche.
  stable?: boolean;
  // Ligne retirée de la commande : barrée, grisée, avec son tag.
  removed?: boolean;
  // Ce que les modifications de la commande ont fait à cette ligne : sa quantité en moins, le remboursement et le motif.
  change?: PdsItemChange;
}>();
const emit = defineEmits<{ toggle: [] }>();

// Une ligne à cocher réserve la place de l'option, même vide : la liste ne saute pas quand on coche.
// Une ligne cochée sans option ne la réserve plus (52 px chez Figma, contre 69 pour les autres).
const tall = computed(
  () =>
    !props.compact &&
    (!!props.item.option ||
      !!props.change ||
      (!!props.selectable && (props.stable || !props.item.prepared))),
);
</script>

<template>
  <component
    :is="selectable ? 'button' : 'div'"
    :type="selectable ? 'button' : undefined"
    :role="selectable ? 'checkbox' : undefined"
    :aria-checked="selectable ? item.prepared : undefined"
    class="border-filet-doux flex w-full gap-3 border-t text-left first:border-t-0"
    :class="[
      selectable && 'cursor-pointer',
      tall
        ? 'min-h-[69px] items-start py-[10px] not-first:min-h-[70px]'
        : 'h-[52px] items-center not-first:h-[53px]',
    ]"
    @click="selectable && emit('toggle')"
  >
    <span v-if="selectable" class="flex h-6 w-6 shrink-0 items-center">
      <PdsCheck :checked="item.prepared" />
    </span>
    <span
      v-if="removed"
      class="flex h-6 w-6 shrink-0 items-center opacity-40"
      aria-hidden="true"
    >
      <PdsCheck :checked="false" />
    </span>
    <span
      class="w-[30px] shrink-0 text-right text-[14px] leading-[1.4] font-semibold"
    >
      {{ item.quantity }}×
    </span>
    <span class="flex min-w-0 flex-1 flex-col gap-1">
      <span
        class="item-name text-[14px] leading-[1.4] font-semibold"
        :class="(item.prepared || removed) && 'item-name-done text-encre-douce'"
      >
        {{ item.name }}
      </span>
      <span
        v-if="item.option && !compact"
        class="text-encre-douce py-1 text-[12px] leading-[1.45]"
      >
        {{ item.option }}
      </span>
      <span v-if="change" class="text-[12px] leading-[1.45]">
        <span class="text-alerte-texte font-semibold">
          <template v-if="change.to > 0"
            >{{ change.from }} → {{ change.to }} · </template
          >−{{ formatMoney(change.refundCents) }}
        </span>
        <span v-if="change.reason" class="text-encre-douce">
          · « {{ change.reason }} »
        </span>
      </span>
    </span>
    <span v-if="removed" class="flex shrink-0 items-center">
      <PdsBadge tone="late">
        Supprimé<template v-if="item.removedAt">
          · {{ formatDateTime(item.removedAt) }}</template
        >
      </PdsBadge>
    </span>
    <span
      v-else-if="change && change.to > 0"
      class="flex shrink-0 items-center"
    >
      <PdsBadge tone="late">
        Modifié · {{ formatDateTime(change.at) }}
      </PdsBadge>
    </span>
  </component>
</template>

<style>
/* Le nom se barre d'un trait qui court, au lieu de s'afficher barré. */
.item-name {
  background-image: linear-gradient(currentColor, currentColor);
  background-repeat: no-repeat;
  background-size: 0% 1px;
  background-position: 0 62%;
  transition:
    background-size 200ms ease-out,
    color 140ms ease;
}

.item-name-done {
  background-size: 100% 1px;
}

@media (prefers-reduced-motion: reduce) {
  .item-name {
    transition: none;
  }
}
</style>
